import React from "react";
import { cn } from "../../../lib/utils";

// Stored values stay standard (RESTRICTED/CONFIDENTIAL/INTERNAL/PUBLIC); the UI shows admin-friendly tiers.
const SENS: Record<string, { label: string; cls: string }> = {
  RESTRICTED: { label: "High", cls: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" },
  CONFIDENTIAL: { label: "Medium", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" },
  INTERNAL: { label: "Low", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  PUBLIC: { label: "None", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
};

export function sensitivityLabel(value?: string): string {
  const s = SENS[String(value || "").toUpperCase()];
  return s ? s.label : "Unknown";
}

// Peak egress sensitivity of one agent skill — matched by producer agent id + skill-name suffix.
export function skillSensitivity(skills: any[] | undefined, agentId?: string, skillName?: string): string {
  if (!agentId || !skillName || !Array.isArray(skills)) return "";
  const n = String(skillName).toLowerCase();
  const s = skills.find((x) => {
    if (String(x.producerAgentId) !== String(agentId)) return false;
    const cn = String(x.capabilityName || "").toLowerCase();
    return cn === n || cn.endsWith("." + n) || cn.endsWith("_" + n) || cn.endsWith(n);
  });
  return s ? s.peakSensitivity : "";
}

// Peak egress sensitivity of one server capability — matched by server + type + public-name suffix.
export function capabilitySensitivity(tools: any[] | undefined, serverName?: string, name?: string, type?: string): string {
  if (!name || !Array.isArray(tools)) return "";
  const n = String(name).toLowerCase();
  const want = type ? String(type).toUpperCase() : null;
  const hit = tools.find((x) => {
    if (x.serverName !== serverName) return false;
    if (want && String(x.capabilityType || "").toUpperCase() !== want) return false;
    const cn = String(x.capabilityName || "").toLowerCase();
    return cn === n || cn.endsWith("." + n) || cn.endsWith("_" + n) || cn.endsWith(n);
  });
  return hit ? hit.peakSensitivity : "";
}

export default function GatewaySensitivityBadge({
  value,
  showUnknown = false,
  tag = false,
  className,
}: {
  value?: string;
  showUnknown?: boolean;
  tag?: boolean;
  className?: string;
}) {
  const s = SENS[String(value || "").toUpperCase()];
  let badge: React.ReactNode = null;
  if (s) {
    badge = (
      <span
        title={`Sensitivity = ${s.label}`}
        className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border", s.cls, className)}
      >
        {s.label}
      </span>
    );
  } else if (showUnknown) {
    badge = (
      <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border bg-card text-muted-foreground border-border shadow-[0_1px_3px_rgb(15_23_42/0.05)]", className)}>
        Unknown
      </span>
    );
  }
  if (!badge) return null;
  if (!tag) return <>{badge}</>;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      Sensitivity {badge}
    </span>
  );
}
